import gsap from "gsap";
import KonamiCode from "konami-code-js";
import { areAchievementsEnabled, setAchievementsEnabled } from "./achievements";

const MS_TO_DISPLAY_TIMER = 2 * 60 * 1000;
const MS_TO_FIRST_MILESTONE = 5 * 60 * 1000;
const MS_TO_SECOND_MILESTONE = 10 * 60 * 1000;
const TIMEOUT_IN_MS = 999 * 60 * 1000 + 59 * 1000;

const DISPLAYED_IDENTIFIER = "timer_displayed";
const TIMER_TIME_IDENTIFIER = "timer_time";

export default class HeartTimer {
	static init() {
		return new HeartTimer();
	}

	constructor() {
		this.timerTime = this.getTimerTime();
		if (!this.timerTime) this.timerTime = this.setTimerTime(new Date(0));

		this.displayed = this.getDisplayed();
		this.ranFirstMilestone = false;
		this.ranSecondMilestone = false;

		this.timerElement = document.querySelector(".heart-timer");
		if (!this.timerElement) return;

		this.timerText = this.timerElement.querySelector("p.timer");
		this.timerSwitch = this.timerElement.querySelector(".switch");
		this.timerSwitchInput = this.timerSwitch.querySelector("input");

		this.timerSwitchInput.addEventListener("change", (event) => {
			if (window.AchievementsEnabled !== undefined) setAchievementsEnabled(event.target.checked);
		});

		this.konamiCode = this.setupKonamiCode();

		this.updateTimerText();
		this.updateVisibility();

		if (this.displayed) gsap.set(this.timerElement, { autoAlpha: 1 });
		if (this.timerSwitch) gsap.set(this.timerSwitch, { autoAlpha: 1 });

		this.timerInterval = this.startTimer();
	}

	startTimer() {
		return setInterval(() => {
			if (!areAchievementsEnabled()) return;
	
			if (!this.ranFirstMilestone && this.timerTime.getTime() >= MS_TO_FIRST_MILESTONE) {
				this.timerElement.classList.add('achMilestone1');
				this.enableAchievement("sound");
				this.ranFirstMilestone = true;
			}
	
			if (!this.ranSecondMilestone && this.timerTime.getTime() >= MS_TO_SECOND_MILESTONE) {
				this.timerElement.classList.add('achMilestone2');
				this.enableAchievement("ghost");
				this.ranFirstMilestone = true;
			}
	
			this.updateVisibility();
			this.updateTimerText();
	
			// Limit timer to a timeout
			if (this.timerTime.getTime() >= TIMEOUT_IN_MS) return this.timerInterval && clearInterval(this.timerInterval);
	
			this.timerTime.setSeconds(+this.timerTime.getSeconds() + 1);
			this.setTimerTime(this.timerTime);
		}, 1000);
	}

	setupKonamiCode() {
		// Setup konami code to unlock achievements
		return new KonamiCode(() => {
			// Set timer time to the milestones' time to unlock the achievement
			if (this.timerTime < MS_TO_FIRST_MILESTONE) this.timerTime = new Date(MS_TO_FIRST_MILESTONE);
			else if (this.timerTime < MS_TO_SECOND_MILESTONE) this.timerTime = new Date(MS_TO_SECOND_MILESTONE);
			else if (window.AchievementsEnabled !== undefined) setAchievementsEnabled(!areAchievementsEnabled()); // At last, toggle between achievements being enabled or no

			if (this.timerTime) this.setTimerTime(this.timerTime);
		});
	}

	updateTimerText() {
		this.timerText.textContent = this.timerTime.getTime() < TIMEOUT_IN_MS ? paddedTime(this.timerTime) : "999:59 min";
	}

	updateVisibility() {
		// Fade in timer when timer once (therefore the global variable "displayed") when time has been reached
		if (!this.displayed && this.timerTime.getTime() >= MS_TO_DISPLAY_TIMER && gsap.getProperty(this.timerElement, 'autoAlpha') <= 0) {
			gsap.to(this.timerElement, { autoAlpha: 1 });

			if (this.timerSwitch) gsap.to(this.timerSwitch, { autoAlpha: 1 });

			this.setDisplayed(true);
		}
	}

	setTimerTime(date) {
		localStorage.setItem(TIMER_TIME_IDENTIFIER, btoa(JSON.stringify(date)));

		return date;
	}

	getTimerTime() {
		let date = localStorage.getItem(TIMER_TIME_IDENTIFIER);

		return !date || date.length === 0 ? null : new Date(JSON.parse(atob(date)));
	}

	setDisplayed(displayed) {
		localStorage.setItem(DISPLAYED_IDENTIFIER, displayed);
	}

	getDisplayed() {
		return localStorage.getItem(DISPLAYED_IDENTIFIER) === "true";
	}

	enableAchievement(slug) {
		if (window.Achievements && !window.Achievements[slug].achieved) window.Achievements[slug].achieve();
	}
}

function paddedTime(timerTime) {
	let seconds = zeroPadInt((timerTime.getTime() / 1000) % 60, 2);
	let minutes = zeroPadInt(timerTime.getTime() / (1000 * 60), 2);

	return `${minutes}:${seconds} min`;
}

function zeroPadInt(number, digits) {
	return new String(parseInt(number)).padStart(digits, "0");
}
